import React, { useState, useRef, useEffect } from 'react'
import { Shield, Key, AlertCircle } from 'lucide-react'

const MfaVerificationModal = ({ email, onVerify, onCancel, loading }) => {
  const [code, setCode] = useState('')
  const [error, setError] = useState('')
  const codeInputRef = useRef(null)

  useEffect(() => {
    setTimeout(() => codeInputRef.current?.focus(), 100)
  }, [])

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!code.trim() || code.length !== 6) {
      setError('Please enter a valid 6-digit code')
      return
    }

    setError('')
    onVerify(code)
  }

  const handleCodeChange = (e) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 6)
    setCode(value)
    setError('')
  }

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" />
        
        <div className="inline-block w-full max-w-sm p-6 my-8 overflow-hidden text-left align-middle transition-all transform bg-white shadow-xl rounded-2xl">
          {/* Header */}
          <div className="flex flex-col items-center text-center mb-6">
            <div className="flex items-center justify-center w-12 h-12 bg-blue-100 rounded-full mb-4">
              <Shield className="w-6 h-6 text-blue-600" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              Two-factor authentication
            </h3>
            <p className="text-sm text-gray-600">
              Enter the 6-digit code from your authenticator app to complete sign in.
            </p>
          </div>

          {/* User Info */}
          <div className="bg-gray-50 rounded-lg p-3 mb-6">
            <div className="flex items-center">
              <Key className="w-4 h-4 text-gray-400 mr-2" />
              <span className="text-sm text-gray-700">{email}</span>
            </div>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit}>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Authentication code
              </label>
              <input
                ref={codeInputRef}
                type="text"
                value={code}
                onChange={handleCodeChange}
                className="w-full px-3 py-3 text-center text-lg font-mono tracking-widest border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="000000"
                maxLength={6}
                inputMode="numeric"
                autoComplete="one-time-code"
                required
              />
              {error && (
                <div className="mt-2 flex items-center text-sm text-red-600">
                  <AlertCircle className="w-4 h-4 mr-1" />
                  {error}
                </div>
              )}
            </div>

            <div className="flex space-x-3">
              <button
                type="button"
                onClick={onCancel}
                disabled={loading}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading || code.length !== 6}
                className="flex-1 px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Verifying...' : 'Verify'}
              </button>
            </div>
          </form>

          {/* Help Text */}
          <div className="mt-6 text-center">
            <p className="text-xs text-gray-500">
              Can't access your authenticator app?{' '}
              <button className="text-blue-600 hover:text-blue-500 underline">
                Use a recovery code
              </button>
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}

export default MfaVerificationModal
